import type { FC } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import {
    getTodayStoreHoliday,
    getTodaysStoreClosingTime,
    getTodaysStoreOpeningTime,
    getTomorrowStoreOpeningTime,
    isStoreOpen,
} from '../../../bff-api/utils/store-utils';
import { FooterStoreHours } from './FooterStoreHours';
import type { Store as StoreModel } from '../../../bff-api/stores/model';

interface Props {
    id: string;
    title: string;
    store?: StoreModel | null;
}

/**
 * This component should be removed once the footer is migrated to the new design.
 *
 * Use app\components\store\Store.tsx instead.
 */
export const FooterStore: FC<Props> = ({ id, title, store }: Props) => {
    if (!store?.id) {
        return null;
    }

    const isOpenStore = isStoreOpen(store);
    const tomorrowOpeningTime = getTomorrowStoreOpeningTime(store, { infix: ' ' }).toUpperCase();
    const todaysOpeningTime = getTodaysStoreOpeningTime(store, { infix: ' ' }).toUpperCase();
    const todaysClosingTime = getTodaysStoreClosingTime(store, { infix: ' ' }).toUpperCase();
    const todaysHoliday = getTodayStoreHoliday(store);

    return (
        <Flex data-testid={`${id}-store-${store.id}`} direction="column" gap={4} minWidth={232}>
            <Text
                data-testid={`${id}-store-${store.id}-title`}
                textStyle="slt-former-heading-micro"
                lineHeight="26px"
                fontSize={18}
                letterSpacing={0.5}
            >
                {title}
            </Text>

            <Flex direction="column" gap={1}>
                <Text
                    data-testid={`${id}-store-${store.id}-name`}
                    textStyle="slt-former-heading-menu"
                    lineHeight="1.25rem"
                    color="slt-black.900"
                >
                    {store.name}
                </Text>

                <Text
                    data-testid={`${id}-store-${store.id}-address1`}
                    textStyle="slt-former-heading-micro"
                    fontWeight={400}
                    lineHeight="1.25rem"
                    color="slt-black.900"
                >
                    {store.address1}
                </Text>

                {store.address2 ? (
                    <Text
                        data-testid={`${id}-store-${store.id}-address2`}
                        textStyle="slt-former-heading-micro"
                        fontWeight={400}
                        lineHeight="1.25rem"
                        color="slt-black.900"
                    >
                        {store.address2}
                    </Text>
                ) : null}

                <Text
                    data-testid={`${id}-store-${store.id}-city_state_postalCode`}
                    textStyle="slt-former-heading-micro"
                    fontWeight={400}
                    lineHeight="1.25rem"
                    color="slt-black.900"
                >
                    {store.city}
                    {store.stateCode && store.postalCode ? (
                        <>
                            {', '}
                            {store.stateCode} {store.postalCode}
                        </>
                    ) : null}
                </Text>

                <Text
                    data-testid={`${id}-store-${store.id}-countryName`}
                    textStyle="slt-former-heading-micro"
                    fontWeight={400}
                    lineHeight="1.25rem"
                    color="slt-black.900"
                >
                    {new Intl.DisplayNames(['en'], { type: 'region' }).of(store.countryCode)}
                </Text>

                <Flex gap={1} marginTop={1}>
                    {isOpenStore ? (
                        <>
                            <Text
                                data-testid={`${id}-store-${store.id}-open-label`}
                                textStyle="slt-former-body"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                                color="slt-black.900"
                            >
                                <FormattedMessage
                                    id="store.item.today.open.until"
                                    defaultMessage="Today open until"
                                />
                                :
                            </Text>

                            <Text
                                data-testid={`${id}-store-${store.id}-open-value`}
                                textStyle="slt-former-body"
                                color="slt-success-green.1000"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                {todaysClosingTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && todaysOpeningTime ? (
                        <>
                            <Text
                                data-testid={`${id}-store-${store.id}-open-today-label`}
                                textStyle="slt-former-body"
                                color="slt-black.900"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                <FormattedMessage
                                    id="store.item.open.today.at"
                                    defaultMessage="Open today at"
                                />
                                :
                            </Text>

                            <Text
                                data-testid={`${id}-store-${store.id}-open-today-value`}
                                textStyle="slt-former-body"
                                color="slt-success-green.1000"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                {todaysOpeningTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && !todaysOpeningTime && tomorrowOpeningTime ? (
                        <>
                            <Text
                                data-testid={`${id}-store-${store.id}-closed-today-label`}
                                textStyle="slt-former-body"
                                color="slt-black.900"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                <FormattedMessage
                                    id="store.item.closed.today.until"
                                    defaultMessage="Closed today until"
                                />
                                :
                            </Text>

                            <Text
                                data-testid={`${id}-store-${store.id}-closed-today-value`}
                                textStyle="slt-former-body"
                                color="slt-red-error"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                {tomorrowOpeningTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && !todaysOpeningTime && !tomorrowOpeningTime ? (
                        <>
                            <Text
                                data-testid={`${id}-store-${store.id}-closed-label`}
                                textStyle="slt-former-body"
                                color="slt-black.900"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                <FormattedMessage id="store.item.today" defaultMessage="Today" />:
                            </Text>

                            <Text
                                data-testid={`${id}-store-${store.id}-closed-value`}
                                textStyle="slt-former-body"
                                color="slt-red-error"
                                lineHeight="20px"
                                letterSpacing="0.5px"
                            >
                                <FormattedMessage
                                    id="store.item.closed.holiday"
                                    defaultMessage="closed{holiday}"
                                    values={{
                                        holiday: todaysHoliday ? ` (${todaysHoliday.label})` : '',
                                    }}
                                />
                            </Text>
                        </>
                    ) : null}
                </Flex>
            </Flex>

            <FooterStoreHours id={`${id}-store-${store.id}-hours`} store={store} />

            <Text
                data-testid={`${id}-store-${store.id}-phone`}
                textStyle="slt-former-heading-micro"
                fontWeight={400}
                lineHeight="20px"
                color="slt-black.900"
            >
                <FormattedMessage id="header.phone" defaultMessage="Phone" />
                {': '}
                {store.phone}
            </Text>
        </Flex>
    );
};

FooterStore.defaultProps = {
    store: undefined,
};
